import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

const PO_STATUS_OPTIONS = [
  'Draft',
  'Internal issue',
  'Acknowledged',
  'Order on Hold',
  'SPO Created',
  'Order Closed',
  'Dispatch in progress',
  'Partially Dispatched',
  'Dispatched',
  'In Transit',
  'Delivered',
  'Replacement in progress',
  'GRN Completed',
  'Payment completed',
]

const PO_STATUS_ALIASES = {
  'Dispatched in progress': 'Dispatch in progress',
  'Dispach in progress': 'Dispatch in progress',
  'Order on hold': 'Order on Hold',
  'Order closed': 'Order Closed',
  'Partially dispatched': 'Partially Dispatched',   
  'In transit': 'In Transit' 
}

function normalizePoStatus(value) {
  if (!value) return 'Draft'
  const trimmed = String(value).trim()
  if (PO_STATUS_OPTIONS.includes(trimmed)) return trimmed
  if (PO_STATUS_ALIASES[trimmed]) return PO_STATUS_ALIASES[trimmed]
  const match = PO_STATUS_OPTIONS.find(
    (option) => option.toLowerCase() === trimmed.toLowerCase()
  )
  return match || trimmed
}

const PAYMENT_STATUS_OPTIONS = ['Pending', 'Paid']
const STATUS_OPTIONS = ['Pending', 'Completed']
const PAYMENT_OPTIONS = ['Credit', 'Advance']
const SUPPLIER_SEARCH_MIN_LENGTH = 3
const SUPPLIER_SEARCH_DEBOUNCE_MS = 400

function parseApiResponse(raw) {
  let responseData = raw
  if (typeof responseData === 'string') {
    try {
      responseData = JSON.parse(responseData)
    } catch {
      return raw
    }
  }
  if (responseData?.body != null) {
    if (typeof responseData.body === 'string') {
      try {
        return JSON.parse(responseData.body)
      } catch {
        return responseData.body
      }
    }
    return responseData.body
  }
  return responseData
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const success = responseData.success
  return (
    success === true || success === 'true' || success === 1 || success === '1'
  )
}

function formatDateForApi(isoDate) {
  if (!isoDate) return ''
  const [yyyy, mm, dd] = isoDate.split('-')
  if (!yyyy || !mm || !dd) return ''
  return `${dd}-${mm}-${yyyy}`
}

function sanitizeDecimalInput(value) {
  const cleaned = String(value ?? '')
    .replace(/,/g, '')
    .replace(/\s/g, '')
    .replace(/[₹$]/g, '')
  if (cleaned === '' || /^\d*\.?\d*$/.test(cleaned)) return cleaned
  return null
}

function parseDecimal(value) {
  const cleaned = sanitizeDecimalInput(value)
  if (cleaned == null || cleaned === '' || cleaned === '.') return NaN
  return parseFloat(cleaned)
}

function parseDateToInput(dateString) {
  if (!dateString) return ''

  const value = String(dateString).trim()

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [dd, mm, yyyy] = value.split('-')
    return `${yyyy}-${mm}-${dd}`
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [dd, mm, yyyy] = value.split('/')
    return `${yyyy}-${mm}-${dd}`
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value
  }

  const d = new Date(value)

  if (isNaN(d.getTime())) {
    return ''
  }
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')

  return `${yyyy}-${mm}-${dd}`
}

function getSupplierRecordId(supplier) {
  return String(
    supplier?.id || supplier?._id || supplier?.supplierId || ''
  ).trim()
}

function getSupplierDisplayName(supplier) {
  return String(
    supplier?.clientname ||
      supplier?.clientName ||
      supplier?.supplierName ||
      supplier?.name ||
      supplier?.companyName ||
      ''
  ).trim()
}

function resolveSupplierFromTracker(tracker) {
  const supplierObj =
    tracker?.supplierId && typeof tracker.supplierId === 'object'
      ? tracker.supplierId
      : null

  const supplierId = String(
    supplierObj?.id ||
      supplierObj?._id ||
      (typeof tracker?.supplierId === 'string' ? tracker.supplierId : '') ||
      ''
  ).trim()

  const supplierName = String(
    tracker?.supplierName ||
      supplierObj?.name ||
      supplierObj?.clientname ||
      ''
  ).trim()

  return { supplierId, supplierName }
}

function extractCustomersList(payload) {
  const data = payload?.data != null ? payload.data : payload
  const list =
    (Array.isArray(data?.customers) && data.customers) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(payload?.customers) && payload.customers) ||
    (Array.isArray(data) && data) ||
    []
  return list
    .map((item) => ({
      _id: String(item?._id || item?.id || '').trim(),
      customerName: String(
        item?.customerName || item?.customer || item?.name || ''
      ).trim()
    }))
    .filter((item) => item._id && item.customerName)
    .sort((a, b) => a.customerName.localeCompare(b.customerName))
}

function resolveCustomerFromTracker(tracker) {
  const customerObj =
    tracker?.customerId && typeof tracker.customerId === 'object'
      ? tracker.customerId
      : null

  const customerId = String(
    customerObj?._id ||
      customerObj?.id ||
      (typeof tracker?.customerId === 'string' ? tracker.customerId : '') ||
      ''
  ).trim()

  const customerName = String(
    tracker?.customerName ||
      customerObj?.customerName ||
      customerObj?.name ||
      ''
  ).trim()

  return { customerId, customerName }
}

const TrackerEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    poNo: '',
    poDate: '',
    materialCode: '',
    itemDesc: '',
    uom: '',
    quantityOrdered: '',
    poStatus: 'Draft',
    spoNo: '',
    spoDate: '',
    supplierName: '',
    supplierId: '',
    cpoBasicPrice: '',
    spoBasicPrice: '',
    payment: 'Credit',
    quantityDispatched: '0',
    dispatchDate: '',
    poReceivedDate:'',
    deliveryDate: '',
    grnStatus: 'Pending',
    paymentStatus: 'Pending',
    projectStatus: 'Pending',
    comments: ''
  })

  const [supplierSearchResults, setSupplierSearchResults] = useState([])
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false)
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
  const [supplierDropdownPos, setSupplierDropdownPos] = useState(null)
  const supplierSearchDebounceRef = useRef(null)
  const supplierSearchRequestIdRef = useRef(0)
  const supplierInputRef = useRef(null)
  const [customers, setCustomers] = useState([])
  const [isCustomersLoading, setIsCustomersLoading] = useState(true)
  const [unitOptions, setUnitOptions] = useState([])
  const [isLoadingUnits, setIsLoadingUnits] = useState(false)

  const fetchCustomers = useCallback(async () => {
    setIsCustomersLoading(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-customers',
        search: '',
        currentUserId: localStorage.getItem('id') || undefined
      })
      const responseData = parseApiResponse(response.data)
      if (isApiSuccess(responseData)) {
        setCustomers(extractCustomersList(responseData))
      } else {
        setCustomers([])
      }
    } catch (error) {
      console.error('Fetch customers error:', error)
      setCustomers([])
      toast.error('Failed to load customers', { autoClose: 2000 })
    } finally {
      setIsCustomersLoading(false)
    }
  }, [])

  const fetchUnits = useCallback(async () => {
    setIsLoadingUnits(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-units',
        page: 1,
        limit: 500,
        sortBy: 'unit',
        sortOrder: 'asc'
      })
      const responseData = parseApiResponse(response.data)
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : []
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || '',
          unit: String(item?.unit || '').trim(),
          status: String(item?.status || '').trim()
        }))
        .filter((item) => item.unit)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === 'active'
        )
      setUnitOptions(isApiSuccess(responseData) ? activeUnits : [])
    } catch (error) {
      console.error('Fetch units error:', error)
      setUnitOptions([])
    } finally {
      setIsLoadingUnits(false)
    }
  }, [])

  useEffect(() => {
    fetchCustomers()
    fetchUnits()
  }, [fetchCustomers, fetchUnits])

  const computedTotal = useMemo(() => {
    const cpo = parseDecimal(formData.cpoBasicPrice)
    const spo = parseDecimal(formData.spoBasicPrice)
    const qty = parseDecimal(formData.quantityOrdered)
    if (!Number.isFinite(cpo) || !Number.isFinite(spo) || !Number.isFinite(qty)) {
      return ''
    }
    return (cpo - spo) * qty
  }, [formData.cpoBasicPrice, formData.spoBasicPrice, formData.quantityOrdered])

  const customerOptions = useMemo(() => {
    const list = [...customers]
    if (
      formData.customerId &&
      !list.some((customer) => customer._id === formData.customerId)
    ) {
      list.unshift({
        _id: formData.customerId,
        customerName: formData.customerName || 'Current customer'
      })
    }
    return list
  }, [customers, formData.customerId, formData.customerName])

  const updateSupplierDropdownPos = useCallback(() => {
    const el = supplierInputRef.current
    if (!el) {
      setSupplierDropdownPos(null)
      return
    }
    const rect = el.getBoundingClientRect()
    setSupplierDropdownPos({
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 260)
    })
  }, [])

  useEffect(() => {
    const fetchTracker = async () => {
      if (!id) {
        toast.error('Tracker ID is missing', { autoClose: 2000 })
        navigate('/trackerUpdate')
        return
      }

      setIsLoadingData(true)
      try {
        const response = await axios.post(API_URL, {
          action: 'get-trackers-data',
          id
        })
        const responseData = parseApiResponse(response.data)

        if (responseData.success === true && responseData.data) {
          const tracker = responseData.data
          const { supplierId, supplierName } = resolveSupplierFromTracker(tracker)
          const { customerId, customerName } = resolveCustomerFromTracker(tracker)
          setFormData({
            customerId,
            customerName,
            poNo: tracker.poNo || '',
            poDate: parseDateToInput(tracker.poDate),
            materialCode: tracker.materialCode || '',
            itemDesc: tracker.itemDesc || '',
            uom: tracker.uom || '',
            quantityOrdered:
              tracker.quantityOrdered != null ? String(tracker.quantityOrdered) : '',
            poStatus: normalizePoStatus(tracker.poStatus),
            spoNo: tracker.spoNo || '',
            spoDate: parseDateToInput(tracker.spoDate),
            supplierName,
            supplierId,
            cpoBasicPrice:
              tracker.cpoBasicPrice != null ? String(tracker.cpoBasicPrice) : '',
            spoBasicPrice:
              tracker.spoBasicPrice != null ? String(tracker.spoBasicPrice) : '',
            payment: tracker.payment || 'Credit',
            quantityDispatched:
              tracker.quantityDispatched != null
                ? String(tracker.quantityDispatched)
                : '0',
            dispatchDate: parseDateToInput(tracker.dispatchDate),
            poReceivedDate: parseDateToInput(tracker.poReceivedDate),
            deliveryDate: parseDateToInput(tracker.deliveryDate),
            grnStatus: tracker.grnStatus || 'Pending',
            paymentStatus: tracker.paymentStatus || 'Pending',
            projectStatus: tracker.projectStatus || 'Pending',
            comments: tracker.comments || ''
          })
        } else {
          toast.error(responseData.message || 'Failed to fetch tracker', {
            autoClose: 2000
          })
          navigate('/trackerUpdate')
        }
      } catch (error) {
        console.error('Fetch tracker error:', error)
        toast.error('Failed to load tracker details', { autoClose: 2000 })
        navigate('/trackerUpdate')
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchTracker()
  }, [id, navigate])

  const searchSuppliers = useCallback(async (searchTerm) => {
    const search = String(searchTerm ?? '').trim()
    if (!search || search.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierSearchResults([])
      return
    }

    const requestId = ++supplierSearchRequestIdRef.current
    setSupplierSearchLoading(true)

    try {
      const userRole = localStorage.getItem('role')
      const payload = {
        action: 'get-suppliers',
        status: 'active',
        search
      }
      if (userRole !== 'admin') {
        payload.status = 'active'
      }

      const response = await axios.post(API_URL, payload)
      if (requestId !== supplierSearchRequestIdRef.current) return

      const responseData = parseApiResponse(response.data)
      const list = responseData?.suppliers || responseData?.data?.suppliers || []

      if (isApiSuccess(responseData) && Array.isArray(list) && list.length > 0) {
        setSupplierSearchResults(list)
        setShowSupplierDropdown(true)
        updateSupplierDropdownPos()
      } else {
        setSupplierSearchResults([])
        setShowSupplierDropdown(true)
        updateSupplierDropdownPos()
      }
    } catch (error) {
      if (requestId !== supplierSearchRequestIdRef.current) return
      console.error('Supplier search error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(errData?.message || 'Failed to search suppliers', {
        autoClose: 2000
      })
      setSupplierSearchResults([])
    } finally {
      if (requestId === supplierSearchRequestIdRef.current) {
        setSupplierSearchLoading(false)
      }
    }
  }, [updateSupplierDropdownPos])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleCustomerChange = (e) => {
    const customerId = String(e.target.value || '').trim()
    const selected = customerOptions.find((c) => c._id === customerId)
    setFormData((prev) => ({
      ...prev,
      customerId,
      customerName: selected?.customerName || ''
    }))
  }

  const handleNumericChange = (e) => {
    const { name, value } = e.target
    const next = sanitizeDecimalInput(value)
    if (next !== null) {
      setFormData((prev) => ({ ...prev, [name]: next }))
    }
  }

  const handleSupplierNameChange = (e) => {
    const value = e.target.value
    setFormData((prev) => ({
      ...prev,
      supplierName: value,
      supplierId: ''
    }))
    setShowSupplierDropdown(true)
    updateSupplierDropdownPos()
    supplierSearchRequestIdRef.current += 1

    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current)
    }

    const trimmed = value.trim()
    if (trimmed.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierSearchLoading(false)
      setSupplierSearchResults([])
      return
    }

    supplierSearchDebounceRef.current = setTimeout(() => {
      searchSuppliers(trimmed)
    }, SUPPLIER_SEARCH_DEBOUNCE_MS)
  }

  const handleSelectSupplier = (supplier) => {
    const name = getSupplierDisplayName(supplier)
    const supplierId = getSupplierRecordId(supplier)
    if (!name || !supplierId) {
      toast.error('Selected supplier is missing name or id', { autoClose: 2000 })
      return
    }
    setFormData((prev) => ({
      ...prev,
      supplierName: name,
      supplierId
    }))
    setSupplierSearchResults([])
    setShowSupplierDropdown(false)
    setSupplierDropdownPos(null)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('[data-supplier-field]') &&
        !event.target.closest('[data-supplier-dropdown]')
      ) {
        setShowSupplierDropdown(false)
        setSupplierDropdownPos(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!showSupplierDropdown) return undefined
    const syncPosition = () => updateSupplierDropdownPos()
    window.addEventListener('resize', syncPosition)
    window.addEventListener('scroll', syncPosition, true)
    return () => {
      window.removeEventListener('resize', syncPosition)
      window.removeEventListener('scroll', syncPosition, true)
    }
  }, [showSupplierDropdown, updateSupplierDropdownPos])

  useEffect(() => {
    return () => {
      if (supplierSearchDebounceRef.current) {
        clearTimeout(supplierSearchDebounceRef.current)
      }
    }
  }, [])

  const validateForm = () => {
    if (!formData.customerId || !formData.customerName.trim()) {
      toast.error('Customer name is required', { autoClose: 2000 })
      return false
    }
    if (!formData.poNo.trim()) {
      toast.error('PO number is required', { autoClose: 2000 })
      return false
    }
    if (!formData.poDate) {
      toast.error('PO date is required', { autoClose: 2000 })
      return false
    }
    if (!formData.materialCode.trim()) {
      toast.error('Material code is required', { autoClose: 2000 })
      return false
    }
    if (!formData.itemDesc.trim()) {
      toast.error('Material description is required', { autoClose: 2000 })
      return false
    }
    if (!formData.uom.trim()) {
      toast.error('UOM is required', { autoClose: 2000 })
      return false
    }
    if (formData.quantityOrdered === '' || isNaN(parseDecimal(formData.quantityOrdered))) {
      toast.error('Valid quantity ordered is required', { autoClose: 2000 })
      return false
    }

    const isSpoCreated = formData.poStatus === 'SPO Created'
    if (isSpoCreated) {
      if (!formData.spoNo.trim()) {
        toast.error('SPO number is required when PO Status is SPO Created', {
          autoClose: 2500
        })
        return false
      }
      if (!formData.spoDate) {
        toast.error('SPO date is required when PO Status is SPO Created', {
          autoClose: 2500
        })
        return false
      }
      if (!formData.supplierName.trim() || !formData.supplierId) {
        toast.error('Supplier is required when PO Status is SPO Created', {
          autoClose: 2500
        })
        return false
      }
      if (
        formData.cpoBasicPrice === '' ||
        isNaN(parseDecimal(formData.cpoBasicPrice))
      ) {
        toast.error('CPO basic price is required when PO Status is SPO Created', {
          autoClose: 2500
        })
        return false
      }
      if (
        formData.spoBasicPrice === '' ||
        isNaN(parseDecimal(formData.spoBasicPrice))
      ) {
        toast.error('SPO basic price is required when PO Status is SPO Created', {
          autoClose: 2500
        })
        return false
      }
      if (!formData.payment) {
        toast.error('Payment is required when PO Status is SPO Created', {
          autoClose: 2500
        })
        return false
      }
    } else {
      if (
        formData.cpoBasicPrice !== '' &&
        isNaN(parseDecimal(formData.cpoBasicPrice))
      ) {
        toast.error('Valid CPO basic price is required', { autoClose: 2000 })
        return false
      }
      if (
        formData.spoBasicPrice !== '' &&
        isNaN(parseDecimal(formData.spoBasicPrice))
      ) {
        toast.error('Valid SPO basic price is required', { autoClose: 2000 })
        return false
      }
    }
  const DISPATCH_STATUSES = ['Partially Dispatched', 'Dispatched', 'In Transit']
    if (DISPATCH_STATUSES.includes(formData.poStatus)) {
      const dispatchedQty = parseDecimal(formData.quantityDispatched)
      const orderedQty = parseDecimal(formData.quantityOrdered)

      if (!Number.isFinite(dispatchedQty) || dispatchedQty <= 0) {
        toast.error(
          `Quantity dispatched is required when PO Status is ${formData.poStatus}`,
          { autoClose: 2500 }
        )
        return false
      }
      if (!formData.dispatchDate) {
        toast.error(
          `Dispatch date is required when PO Status is ${formData.poStatus}`,
          { autoClose: 2500 }
        )
        return false
      }
      if (
        formData.poStatus === 'Partially Dispatched' &&
        Number.isFinite(orderedQty) &&
        dispatchedQty >= orderedQty
      ) {
        toast.error(
          'For Partially Dispatched, quantity dispatched must be less than quantity ordered',
          { autoClose: 2500 }
        )
        return false
      }
    }
    if (
      formData.quantityDispatched !== '' &&
      isNaN(parseFloat(formData.quantityDispatched))
    ) {
      toast.error('Valid quantity dispatched is required', { autoClose: 2000 })
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => navigate('/'), 2000)
      return
    }

    const cpoBasicPrice = parseDecimal(formData.cpoBasicPrice) || 0
    const spoBasicPrice = parseDecimal(formData.spoBasicPrice) || 0
    const quantityOrdered = parseDecimal(formData.quantityOrdered) || 0
    const total = (cpoBasicPrice - spoBasicPrice) * quantityOrdered

    setIsLoading(true)

    try {
      const payload = {
        action: 'update-trackers',
        id,
        userid: userId,
        customerName: formData.customerName.trim(),
        customerId: formData.customerId,
        poNo: formData.poNo.trim(),
        poDate: formatDateForApi(formData.poDate),
        materialCode: formData.materialCode.trim(),
        itemDesc: formData.itemDesc.trim(),
        uom: formData.uom.trim(),
        quantityOrdered,
        poStatus: formData.poStatus,
        spoNo: formData.spoNo.trim(),
        spoDate: formatDateForApi(formData.spoDate),
        supplierName: formData.supplierName.trim(),
        supplierId: formData.supplierId,
        cpoBasicPrice,
        spoBasicPrice,
        total,
        payment: formData.payment,
        quantityDispatched: parseFloat(formData.quantityDispatched) || 0,
        dispatchDate: formData.dispatchDate
          ? formatDateForApi(formData.dispatchDate)
          : '',
        deliveryDate: formData.deliveryDate
          ? formatDateForApi(formData.deliveryDate)
          : '',
           poReceivedDate: formData.poReceivedDate
          ? formatDateForApi(formData.poReceivedDate)
          : '',
        grnStatus: formData.grnStatus,
        paymentStatus: formData.paymentStatus,
        projectStatus: formData.projectStatus,
        comments: formData.comments.trim()
      }

      const response = await axios.post(API_URL, payload)
      const responseData = parseApiResponse(response.data)

      if (responseData.success === true) {
        toast.success(responseData.message || 'Tracker updated successfully', {
          autoClose: 2000
        })
        setTimeout(() => navigate('/trackerUpdate'), 2000)
      } else {
        toast.error(responseData.message || 'Failed to update tracker', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Update tracker error:', error)
      if (error.response?.data) {
        const errorData = parseApiResponse(error.response.data)
        toast.error(errorData.message || 'An error occurred while updating tracker', {
          autoClose: 2000
        })
      } else {
        toast.error('Network error. Please check your connection and try again.', {
          autoClose: 2000
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingData) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Edit Customer Report</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/trackerUpdate">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="frmwdth_cntr">
              <div className="card card-primary card-outline">
                <div className="card-body text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const spoRequired = formData.poStatus === 'SPO Created'

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Customer Report</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/trackerUpdate">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Customer Name</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="customerId"
                            className="form-control"
                            value={formData.customerId}
                            onChange={handleCustomerChange}
                            disabled={isLoading || isLoadingData || isCustomersLoading}
                            required
                          >
                            <option value="">
                              {isCustomersLoading
                                ? 'Loading customers...'
                                : customerOptions.length === 0
                                  ? 'No customers available'
                                  : 'Select customer'}
                            </option>
                            {customerOptions.map((customer) => (
                              <option key={customer._id} value={customer._id}>
                                {customer.customerName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">PO No</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="poNo"
                            className="form-control"
                            value={formData.poNo}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">PO Date</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="poDate"
                            className="form-control"
                            value={formData.poDate}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Material Code</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="materialCode"
                            className="form-control"
                            value={formData.materialCode}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Material Description</label>
                        <div className="col-12 col-md-8">
                          <textarea
                            name="itemDesc"
                            className="form-control"
                            value={formData.itemDesc}
                            onChange={handleChange}
                            rows={3}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">UOM</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="uom"
                            className="form-control"
                            value={formData.uom}
                            onChange={handleChange}
                            disabled={isLoading || isLoadingUnits}
                            required
                          >
                            <option value="">
                              {isLoadingUnits ? 'Loading...' : 'Select UOM'}
                            </option>
                            {formData.uom &&
                              !unitOptions.some(
                                (opt) =>
                                  opt.unit.toLowerCase() ===
                                  String(formData.uom).toLowerCase()
                              ) && (
                                <option value={formData.uom}>{formData.uom}</option>
                              )}
                            {unitOptions.map((opt) => (
                              <option key={opt.id || opt.unit} value={opt.unit}>
                                {opt.unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Quantity Ordered</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="quantityOrdered"
                            className="form-control"
                            value={formData.quantityOrdered}
                            onChange={handleNumericChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">PO Status</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="poStatus"
                            className="form-control"
                            value={formData.poStatus}
                            onChange={handleChange}
                          >
                            {PO_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                            {!PO_STATUS_OPTIONS.includes(formData.poStatus) && formData.poStatus && (
                              <option value={formData.poStatus}>{formData.poStatus}</option>
                            )}
                          </select>
                        </div>
                      </div>
                       <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">PO Received Date</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="poReceivedDate"
                            className="form-control"
                            value={formData.poReceivedDate}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          SPO No
                          {spoRequired ? <span className="text-danger"> *</span> : null}
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="spoNo"
                            className="form-control"
                            value={formData.spoNo}
                            onChange={handleChange}
                            required={spoRequired}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          SPO Date
                          {spoRequired ? <span className="text-danger"> *</span> : null}
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="spoDate"
                            className="form-control"
                            value={formData.spoDate}
                            onChange={handleChange}
                            required={spoRequired}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Supplier Name
                          {spoRequired ? <span className="text-danger"> *</span> : null}
                        </label>
                        <div className="col-12 col-md-8" data-supplier-field>
                          <input
                            ref={supplierInputRef}
                            type="text"
                            name="supplierName"
                            className="form-control"
                            placeholder="Search supplier (min 3 characters)"
                            value={formData.supplierName}
                            onChange={handleSupplierNameChange}
                            onFocus={() => {
                              setShowSupplierDropdown(true)
                              updateSupplierDropdownPos()
                            }}
                            autoComplete="off"
                            required={spoRequired}
                          />
                          {supplierSearchLoading && (
                            <small className="text-muted">Searching...</small>
                          )}
                          {formData.supplierId ? (
                            <small className="text-success d-block">
                              Supplier selected
                            </small>
                          ) : (
                            <small className="text-muted d-block">
                              Select a supplier from the list
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          CPO Basic Price
                          {spoRequired ? <span className="text-danger"> *</span> : null}
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="cpoBasicPrice"
                            className="form-control"
                            value={formData.cpoBasicPrice}
                            onChange={handleNumericChange}
                            required={spoRequired}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          SPO Basic Price
                          {spoRequired ? <span className="text-danger"> *</span> : null}
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="spoBasicPrice"
                            className="form-control"
                            value={formData.spoBasicPrice}
                            onChange={handleNumericChange}
                            required={spoRequired}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Total Margin</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            value={
                              computedTotal === ''
                                ? ''
                                : Number(computedTotal).toFixed(
                                    Number.isInteger(computedTotal) ? 0 : 2
                                  )
                            }
                            readOnly
                            disabled
                            placeholder="(CPO - SPO) × Qty"
                          />
                          <small className="text-muted">
                            (CPO Basic - SPO Basic) × Qty Ordered
                          </small>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Payment
                          {spoRequired ? <span className="text-danger"> *</span> : null}
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="payment"
                            className="form-control"
                            value={formData.payment}
                            onChange={handleChange}
                            required={spoRequired}
                          >
                            <option value="">
                              {spoRequired ? 'Select Payment *' : 'Select Payment'}
                            </option>
                            {PAYMENT_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Comments</label>
                        <div className="col-12 col-md-8">
                          <textarea
                            name="comments"
                            className="form-control"
                            value={formData.comments}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Add any comments or notes..."
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Quantity Dispatched</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="quantityDispatched"
                            className="form-control"
                            value={formData.quantityDispatched}
                            onChange={handleNumericChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Dispatch Date</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="dispatchDate"
                            className="form-control"
                            value={formData.dispatchDate}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Delivery Date</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="deliveryDate"
                            className="form-control"
                            value={formData.deliveryDate}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">GRN Status</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="grnStatus"
                            className="form-control"
                            value={formData.grnStatus}
                            onChange={handleChange}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Payment Status</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="paymentStatus"
                            className="form-control"
                            value={formData.paymentStatus}
                            onChange={handleChange}
                          >
                            {PAYMENT_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Project Status</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="projectStatus"
                            className="form-control"
                            value={formData.projectStatus}
                            onChange={handleChange}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      <button type="submit" className="btn btn-blue" disabled={isLoading}>
                        {isLoading ? 'Updating...' : 'Update'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSupplierDropdown &&
        supplierDropdownPos &&
        (supplierSearchResults.length > 0 ||
          (formData.supplierName.trim().length >= SUPPLIER_SEARCH_MIN_LENGTH &&
            !supplierSearchLoading)) &&
        createPortal(
          <div
            data-supplier-dropdown
            style={{
              position: 'fixed',
              top: supplierDropdownPos.top,
              left: supplierDropdownPos.left,
              width: supplierDropdownPos.width,
              maxHeight: '220px',
              overflowY: 'auto',
              backgroundColor: '#fff',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              zIndex: 3000,
              boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
            }}
          >
            {supplierSearchResults.length > 0 ? (
              supplierSearchResults.map((supplier) => {
                const supplierId = getSupplierRecordId(supplier)
                const name = getSupplierDisplayName(supplier)
                return (
                  <div
                    key={supplierId || name}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleSelectSupplier(supplier)
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: '13px'
                    }}
                  >
                    <strong>{name || '-'}</strong>
                    {supplier.email ? (
                      <div className="text-muted" style={{ fontSize: '12px' }}>
                        {supplier.email}
                      </div>
                    ) : null}
                  </div>
                )
              })
            ) : (
              <div
                style={{
                  padding: '8px 12px',
                  color: '#6c757d',
                  fontSize: '13px'
                }}
              >
                No suppliers found
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  )
}

export default TrackerEdit